import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, KeyRound, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/memberAuth";
import { startGoogle, startApple } from "./SocialButtons";

const inp = "w-full border border-black/15 rounded-xl px-4 py-3 text-sm focus:border-[#E63946] outline-none";

export const SecuritySection = () => {
  const [sec, setSec] = useState(null);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/members/security").then((r) => setSec(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!sec) return <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div>;

  const addPassword = async () => {
    setBusy(true);
    try { await api.post("/members/security/add-password", { password: pw }); toast.success(sec.password ? "Password changed" : "Password added"); setPw(""); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };
  const disconnect = async (provider) => {
    try { await api.post("/members/security/disconnect", { provider }); toast.success(`${provider === "google" ? "Google" : "Apple"} disconnected`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const Row = ({ label, connected, email, onConnect, onDisconnect, testid }) => (
    <div className="flex items-center gap-3 bg-white border border-black/10 rounded-xl p-4" data-testid={testid}>
      <ShieldCheck size={18} className={connected ? "text-emerald-500" : "text-black/25"} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{label}</p>
        <p className="text-xs text-black/45 truncate">{connected ? (email || "Connected") : "Not connected"}</p>
      </div>
      {connected ? (
        onDisconnect ? <button onClick={onDisconnect} className="inline-flex items-center gap-1.5 text-xs text-[#E63946] rounded-full border border-[#E63946]/40 px-3.5 py-2 hover:bg-[#E63946]/5"><Unlink size={12} /> Disconnect</button>
          : <span className="text-xs text-emerald-600 font-medium">Active</span>
      ) : (
        onConnect ? <button onClick={onConnect} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-black/15 px-3.5 py-2 hover:bg-black/5"><Link2 size={12} /> Connect</button> : null
      )}
    </div>
  );

  return (
    <div className="mt-10 max-w-xl" data-testid="security-section">
      <h3 className="font-display font-bold text-lg mb-1">Sign-In & Security</h3>
      <p className="text-xs text-black/45 mb-4">Connect multiple sign-in methods — they all open the same One Love account. We never see or store your Google or Apple password.</p>
      <div className="space-y-2.5">
        <Row label="Google" connected={sec.google.connected} email={sec.google.email} testid="security-google-row"
          onConnect={() => startGoogle("link")}
          onDisconnect={sec.google.connected ? () => disconnect("google") : null} />
        {sec.apple_available || sec.apple.connected ? (
          <Row label="Apple" connected={sec.apple.connected} email={sec.apple.email} testid="security-apple-row"
            onConnect={sec.apple_available ? () => startApple("link") : null}
            onDisconnect={sec.apple.connected ? () => disconnect("apple") : null} />
        ) : null}
        <div className="bg-white border border-black/10 rounded-xl p-4" data-testid="security-password-row">
          <div className="flex items-center gap-3 mb-3">
            <KeyRound size={18} className={sec.password ? "text-emerald-500" : "text-black/25"} />
            <div className="flex-1"><p className="font-medium text-sm">Email & Password</p>
              <p className="text-xs text-black/45">{sec.password ? "Active" : "No password set — add one as a backup sign-in method"}</p></div>
          </div>
          <div className="flex gap-2">
            <input type="password" className={inp} placeholder={sec.password ? "New password (min 8 characters)" : "Create a password (min 8 characters)"}
              value={pw} onChange={(e) => setPw(e.target.value)} data-testid="add-password-input" />
            <button onClick={addPassword} disabled={busy || pw.length < 8} data-testid="add-password-btn"
              className="rounded-full bg-[#0A0A0A] text-white px-5 text-sm hover:bg-[#E63946] transition-colors disabled:opacity-40 shrink-0">{sec.password ? "Change" : "Add"}</button>
          </div>
        </div>
      </div>
      {sec.recent_activity?.length > 0 && (
        <div className="mt-6">
          <p className="text-xs uppercase tracking-wider text-black/40 mb-2">Recent Account Activity</p>
          <div className="space-y-1">
            {sec.recent_activity.map((a) => (
              <p key={a.id} className="text-xs text-black/50">{new Date(a.at).toLocaleString()} — {a.action.replace(/_/g, " ")}{a.detail ? ` (${a.detail})` : ""}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
